<template>
  <div>
    <el-popover
      :visible="isOpen"
      trigger="click"
      placement="bottom-end"
      popper-class="messenger tw-overflow-hidden"
      :width="458"
      @show="$store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: null, foreignerId: null }])"
      @hide="isNewThreadDrawerOpen = false"
    >
      <template #reference>
        <div class="tw-fixed tw-bottom-10 tw-right-10">
          <i-badge
            v-if="countUnseenThreads"
            type="primary"
            is-circle
            class="tw-absolute tw-z-10 tw--top-1.5 tw--right-1.5 tw-border-2 tw-border-white dark:tw-border-dm"
          >
            {{ countUnseenThreads }}
          </i-badge>
          <i-button
            type="primary"
            is-icon
            @click="isOpen = !isOpen"
          >
            <icon-close
              v-if="isOpen"
              class="tw-pointer-events-none"
            />
            <icon-message
              v-else
              class="tw-pointer-events-none"
            />
          </i-button>
        </div>
      </template>

      <div>
        <header class="tw-px-6 tw-py-5 tw-leading-0 tw-bg-light dark:tw-bg-dm-light">
          <div class="tw-flex tw-items-center tw-gap-3">
            <template v-if="openedThreadData.id">
              <i-button
                type="white"
                size="medium"
                is-icon
                @click="() => $store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: null, foreignerId: null }])"
              >
                <icon-angle-left />
              </i-button>
            </template>
            <template v-else>
              <ForeignerFamilyPopoverList
                v-if="foreigner && foreigner.family_profiles && foreigner.family_profiles.length"
                :family="foreigner.family_profiles.map((item) => ({
                  avatar_url: item.avatar_url,
                  relation_with_id: item.id,
                  relation_with_fullname: item.fullname,
                  relation_type: item.relation_type_name,
                  relation_type_name: $store.getters['v2/shared/enums/get']('family_relations', item.relation_type) ? $store.getters['v2/shared/enums/get']('family_relations', item.relation_type).name : '',
                }))"
                show-messages-count
                :foreigner-id="foreigner.id"
                class="tw-leading-[0px]"
                :link="(id) => `/foreigner/${id}`"
                store-path="v2/foreigner/messaging"
                :redirect-on-family-member-click="false"
                @family-member-click="handleFamilyMemberChange"
              >
                <i-user-avatar
                  :avatar="foreigner.avatar_url && foreigner.avatar_url['128'] && `${$baseHost}/${foreigner.avatar_url['128']}`"
                  :fullname="foreigner.fullname"
                  size="mini"
                  class="tw-cursor-pointer"
                />
              </ForeignerFamilyPopoverList>
            </template>
            <ForeignerMessagingFilterSearch
              v-if="openedThreadData.id"
              class="tw-flex-1"
            />
            <ForeignerMessagingFulltextSearch
              v-else
              class="tw-flex-1"
            />
            <template v-if="openedThreadData.id">
              <i-button
                type="white"
                size="medium"
                is-icon
                :is-active="isFiltersOpen"
                @click="isFiltersOpen = !isFiltersOpen"
              >
                <icon-filters />
              </i-button>
            </template>
            <router-link
              :to="fullScreenLink"
            >
              <i-button
                type="white"
                size="medium"
                is-icon
              >
                <icon-fullscreen />
              </i-button>
            </router-link>
          </div>
          <ForeignerMessagingFilters
            v-if="openedThreadData.id && isFiltersOpen"
            class="tw-pt-3"
          />
        </header>
        <div>
          <ForeignerMessagingMessages
            v-if="openedThreadData.id"
            :permit-id="openedThreadData.id"
            :show-permit="openedThreadData.id === 'all'"
            :show-send-message="openedThreadData.id !== 'all'"
            :are-filters-expanded="isFiltersOpen"
          />
          <div
            v-else
            ref="ForeignerMessagingThreads"
            class="ForeignerMessagingThreads tw-px-6 tw-divide-y tw-divide-gray dark:tw-divide-dm-gray tw-overflow-auto"
            :class="[
              showAllThreadsButton ? 'has-all-threads-button' : '',
            ]"
            @scroll="handleScroll"
          >
            <template v-if="fulltextSearchValue.length">
              <ForeignerMessagingThread
                v-for="message in $store.state.v2.foreigner.messaging.fulltextSearchResults"
                :key="message.id"
                :thread="message"
                :is-active="message.id === $store.state.v2.foreigner.messaging.fulltextMessageId"
                @click.native="() => {
                  $store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: String(message.permit_id), foreignerId: message.foreigner_id }]);
                  $store.commit('v2/foreigner/messaging/set', ['fulltextMessageId', message.id]);
                }"
              />
            </template>

            <template v-else>
              <div
                v-if="threads.length === 0"
                class="tw-flex tw-h-full tw-justify-center tw-items-center"
              >
                {{ $t('messaging.no_threads') }}
              </div>
              <ForeignerMessagingThread
                v-for="thread in threads"
                :key="thread.id"
                :thread="thread"
                @click.native="() => $store.commit('v2/foreigner/messaging/set', ['openedThreadData', { id: String(thread.permit_id),foreignerId: thread.foreigner_id}])"
              />
            </template>
            <div
              v-if="moreThreadsUrl"
              v-loading="true"
              class="tw-w-full tw-h-14"
            />
          </div>
        </div>
        <i-button
          v-if="!openedThreadData.id"
          class="tw-flex tw-items-center tw-justify-center tw-w-full tw-rounded-t-none"
          @click="() => {
            $store.commit('v2/foreigner/messaging/set', ['openedThreadData', { foreignerId: null, id: 'all' }]);
            $store.commit('v2/foreigner/messaging/set', ['fulltextSearchValue', '']);
            $store.commit('v2/foreigner/messaging/set', ['fulltextMessageId', null]);
          }"
        >
          <span class="tw-font-bold">
            {{ $t('messaging.all_threads') }}
          </span>
        </i-button>
        <i-button
          v-if="showAllThreadsButton"
          class="tw-absolute tw-bottom-16 tw-right-2 tw-shadow tw-border-none"
          is-icon
          type="white"
          @click="isNewThreadDrawerOpen = true"
        >
          <icon-plus />
        </i-button>
        <ForeignersNewThreadDrawer
          :is-open="isNewThreadDrawerOpen"
          :can-be-opened-threads="canBeOpenedThreads"
          store-path="v2/foreigner/messaging"
          @close="isNewThreadDrawerOpen = false"
        />
      </div>
    </el-popover>
  </div>
</template>

<script>
import ForeignerFamilyPopoverList from '@/components/Foreigner/ForeignerFamilyPopoverList.vue';
import ForeignerMessagingFilters from '@/components/Foreigner/Messaging/ForeignerMessagingFilters.vue';
import ForeignerMessagingMessages from '@/components/Foreigner/Messaging/ForeignerMessagingMessages.vue';
import ForeignerMessagingThread from '@/components/Foreigner/Messaging/ForeignerMessagingThread.vue';
import ForeignerMessagingFilterSearch from '@/components/Foreigner/Messaging/ForeignerMessagingFilterSearch.vue';
import ForeignerMessagingFulltextSearch from '@/components/Foreigner/Messaging/ForeignerMessagingFulltextSearch.vue';
import ForeignersNewThreadDrawer from '@/components/Foreigners/Messaging/ForeignersNewThreadDrawer.vue';
import { mapGetters, mapState } from 'vuex';

export default {
  components: {
    ForeignersNewThreadDrawer,
    ForeignerFamilyPopoverList,
    ForeignerMessagingFilters,
    ForeignerMessagingMessages,
    ForeignerMessagingThread,
    ForeignerMessagingFilterSearch,
    ForeignerMessagingFulltextSearch,
  },

  data() {
    return {
      isOpen: false,
      isFiltersOpen: false,
      timer: null,
      isNewThreadDrawerOpen: false,
      isMoreThreadsLoading: false,
      popoverTrigger: 'manual',
    };
  },

  computed: {
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      foreignerDataFromForeignerPage: (state) => state.v2.foreigner.foreigner.foreigner,
      foreigner: (state) => state.v2.foreigner.messaging.foreigner,
      moreThreadsUrl: (state) => state.v2.foreigner.messaging.moreThreadsUrl,
      countUnseenThreads: (state) => state.v2.foreigner.messaging.threads.filter(({ unseen_messages }) => unseen_messages).length,
      openedThreadData: (state) => state.v2.foreigner.messaging.openedThreadData,
      fulltextSearchValue: (state) => state.v2.foreigner.messaging.fulltextSearchValue,
    }),
    ...mapGetters({
      threads: 'v2/foreigner/messaging/threads',
      openedThread: 'v2/foreigner/messaging/getOpenedThreadPermit',
      canBeOpenedThreads: 'v2/foreigner/messaging/canBeOpenedThreads',
    }),
    showAllThreadsButton() {
      return this.foreigner && !this.openedThreadData.id && this.canBeOpenedThreads.length !== 0;
    },
    fullScreenLink() {
      const myThreadsOpened = this.foreigner?.id === this.me.id;
      const someThreadIsOpened = this.openedThreadData.id != null;

      let url = myThreadsOpened ? '/messaging' : `family/${this.foreigner?.id}/messaging`;

      if (someThreadIsOpened) {
        url += `/${this.openedThreadData.id}`;
      }

      return url;
    },
  },

  watch: {
    foreignerDataFromForeignerPage(newForeigner) {
      if (newForeigner) {
        this.$store.commit('v2/foreigner/messaging/set', ['foreigner', newForeigner]);
        this.$store.dispatch('v2/foreigner/messaging/loadForeignerPermits', newForeigner.id);
      }
    },
    foreigner: {
      handler() {
        this.loadThreads();
      },
      immediate: true,
      deep: true,
    },
    'openedThread.foreigner_id'(newThreadForeignerId) {
      if (newThreadForeignerId) {
        this.$store.dispatch('v2/foreigner/messaging/loadMessagingUsers', { foreignerId: newThreadForeignerId });
      }
    },
  },

  mounted() {
    window.addEventListener('foreignerMessagingPopoverOpen', this.openPopover);
    if (this.foreignerDataFromForeignerPage) {
      this.$store.commit('v2/foreigner/messaging/set', ['foreigner', this.foreignerDataFromForeignerPage]);
      this.$store.dispatch('v2/foreigner/messaging/loadForeignerPermits', this.foreignerDataFromForeignerPage.id);
      this.loadThreads();
    } else {
      this.handleFamilyMemberChange();
    }
    this.$store.dispatch('v2/foreigner/messaging/loadMessagingUsers');
    this.timer = setInterval(() => {
      this.loadThreads();
    }, 30 * 1000);
  },
  beforeUnmount() {
    window.removeEventListener('foreignerMessagingPopoverOpen', this.openPopover);
    clearTimeout(this.timer);
    this.isNewThreadDrawerOpen = false;
  },

  methods: {
    openPopover() {
      this.isOpen = true;
    },
    async loadThreads() {
      if(!this.foreigner?.id ) {
        return;
      }
      await this.$store.dispatch('v2/foreigner/messaging/loadThreads', this.foreigner.id);
    },
    async loadMoreThreads() {
      this.isMoreThreadsLoading = true;
      await this.$store.dispatch('v2/foreigner/messaging/loadMoreThreads');
      this.isMoreThreadsLoading = false;
    },
    handleScroll() {
      const container = this.$refs.ForeignerMessagingThreads;
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight; // Adjust for visible height

      if (this.moreThreadsUrl && !this.isMoreThreadsLoading && distanceToBottom < 10) {
        this.loadMoreThreads();
      }
    },
    async handleFamilyMemberChange(foreignerId) {
      if (foreignerId) {
        await this.$store.dispatch('v2/foreigner/messaging/loadForeignerDetail', foreignerId);
        this.$store.dispatch('v2/foreigner/messaging/loadForeignerPermits', foreignerId);
      }
      await this.loadThreads();
    },
  },
};
</script>

<style lang="scss" scoped>
.ForeignerMessagingThreads {
  height: 60vh;
  overscroll-behavior: none;

  &.has-all-threads-button {
    height: calc(60vh - 48px);
  }
}
</style>
